import { Storage } from '@capacitor/storage';
import { auth, firebase } from '../config/firebase.config';
import { LoginResponse } from './AuthService';

// Constants for storage keys
const FIREBASE_TOKEN_KEY = 'firebase_token';
const TOKEN_EXPIRY_KEY = 'firebase_token_expiry';
const TOKEN_PAYLOAD_KEY = 'firebase_token_payload';

interface TokenPayload {
  uid: string;
  ced: string;
  eventcode: string;
  role: string;
  exp: number;
  iat: number;
}

export class FirebaseTokenService {
  private static instance: FirebaseTokenService;

  /**
   * Obtiene la URL del endpoint de Firebase token
   * En desarrollo usa proxy para evitar CORS
   */
  private getFirebaseTokenEndpoint(): string {
    // En desarrollo, usar proxy para evitar CORS
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return '/api/firebase-token';
    }

    // En producción, usar URL directa
    return 'https://esimposio.com/miesimposio/app_login/firebase_token_simple.php';
  }

  /**
   * Obtiene la URL del endpoint de validación de token
   */
  private getValidateTokenEndpoint(): string {
    // En desarrollo, usar proxy para evitar CORS
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return '/api/validate-token';
    }

    // En producción, usar URL directa
    return 'https://esimposio.com/miesimposio/app_login/validate_token.php';
  }
  private tokenExpiryTime: number = 0;
  private isRefreshing: boolean = false;
  private tokenRefreshPromise: Promise<string | null> | null = null;
  private tokenPayload: TokenPayload | null = null;

  private constructor() {}

  static getInstance(): FirebaseTokenService {
    if (!FirebaseTokenService.instance) {
      FirebaseTokenService.instance = new FirebaseTokenService();
    }
    return FirebaseTokenService.instance;
  }

  /**
   * Obtiene un token de Firebase después de la autenticación con la API personalizada
   */
  async getFirebaseToken(userData: LoginResponse): Promise<string | null> {
    try {
      // Verificar si ya tenemos un token válido en almacenamiento
      const storedToken = await this.getStoredToken();
      if (storedToken) {
        return storedToken;
      }

      // Si no hay token válido, solicitar uno nuevo
      return await this.requestNewToken(userData);
    } catch (error) {
      console.error('Error al obtener token de Firebase:', error);
      return null;
    }
  }

  /**
   * Verifica si hay un token almacenado y si aún es válido
   */
  private async getStoredToken(): Promise<string | null> {
    try {
      const tokenResponse = await Storage.get({ key: FIREBASE_TOKEN_KEY });
      const expiryResponse = await Storage.get({ key: TOKEN_EXPIRY_KEY });
      const payloadResponse = await Storage.get({ key: TOKEN_PAYLOAD_KEY });

      const token = tokenResponse.value;
      const expiry = expiryResponse.value ? parseInt(expiryResponse.value, 10) : 0;

      // Verificar si el token existe y no ha expirado (con 5 minutos de margen)
      const now = Date.now();
      if (token && expiry > now + 5 * 60 * 1000) {
        this.tokenExpiryTime = expiry;

        // Cargar el payload del token si está disponible
        if (payloadResponse.value) {
          try {
            this.tokenPayload = JSON.parse(payloadResponse.value);
          } catch (e) {
            console.warn('Error al parsear payload del token:', e);
          }
        }

        return token;
      }

      return null;
    } catch (error) {
      console.error('Error al recuperar token almacenado:', error);
      return null;
    }
  }

  /**
   * Solicita un nuevo token al servidor de autenticación
   */
  private async requestNewToken(userData: LoginResponse): Promise<string | null> {
    // Si ya hay una solicitud en curso, esperar a que termine
    if (this.isRefreshing && this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.isRefreshing = true;
    this.tokenRefreshPromise = this._requestNewToken(userData);

    try {
      return await this.tokenRefreshPromise;
    } finally {
      this.isRefreshing = false;
      this.tokenRefreshPromise = null;
    }
  }

  /**
   * Implementación real de la solicitud de token
   */
  private async _requestNewToken(userData: LoginResponse): Promise<string | null> {
    try {
      // Preparar los datos para la solicitud
      const tokenRequest = {
        uid: userData.id,
        ced: userData.ced,
        eventcode: userData.eventcode,
        client: '36'
      };

      // URL del endpoint simplificado para obtener tokens
      const response = await fetch(this.getFirebaseTokenEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tokenRequest)
      });

      if (!response.ok) {
        throw new Error(`Error de red: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.token) {
        throw new Error('No se recibió token');
      }

      // Extraer el payload del token JWT
      const tokenPayload = this.parseJwt(data.token);
      if (!tokenPayload) {
        throw new Error('Token inválido');
      }

      // Calcular tiempo de expiración desde el payload o usar el valor por defecto
      const expiryTime = tokenPayload.exp ? tokenPayload.exp * 1000 : (Date.now() + (data.expiresIn || 3600) * 1000);

      // Almacenar el token, su payload y su tiempo de expiración
      await Storage.set({ key: FIREBASE_TOKEN_KEY, value: data.token });
      await Storage.set({ key: TOKEN_EXPIRY_KEY, value: expiryTime.toString() });
      await Storage.set({ key: TOKEN_PAYLOAD_KEY, value: JSON.stringify(tokenPayload) });

      this.tokenExpiryTime = expiryTime;
      this.tokenPayload = tokenPayload;

      // Inicializar Firebase con autenticación anónima (ya que no usamos Firebase Auth directamente)
      await this.initializeFirebaseAuth();

      return data.token;
    } catch (error) {
      console.error('Error al solicitar token:', error);

      // Si el error es porque el endpoint no existe, usar el modo de compatibilidad
      if (error instanceof Error && error.message.includes('404')) {
        console.warn('Endpoint de token no encontrado, usando modo de compatibilidad');
        return this.useCompatibilityMode(userData);
      }

      return null;
    }
  }

  /**
   * Inicializa Firebase Auth (usando autenticación anónima como base)
   */
  private async initializeFirebaseAuth(): Promise<void> {
    try {
      // Usar autenticación anónima como base para Firebase
      await auth.signInAnonymously();
    } catch (error) {
      console.error('Error al inicializar Firebase Auth:', error);
    }
  }

  /**
   * Parsea un token JWT para extraer su payload
   */
  private parseJwt(token: string): TokenPayload | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error al parsear token JWT:', error);
      return null;
    }
  }

  /**
   * Modo de compatibilidad para cuando no existe el endpoint de tokens
   */
  private async useCompatibilityMode(userData: LoginResponse): Promise<string | null> {
    try {
      // En modo de compatibilidad, intentamos iniciar sesión anónima en Firebase
      await auth.signInAnonymously();

      // Crear un token JWT simple
      const payload = {
        uid: userData.id || 'anonymous',
        ced: userData.ced || 'anonymous',
        eventcode: userData.eventcode || 'anonymous',
        role: 'anonymous',
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 900 // 15 minutos
      };

      // Almacenar un token ficticio con expiración corta
      const expiryTime = payload.exp * 1000;
      const fakeToken = 'anonymous-auth-token';

      await Storage.set({ key: FIREBASE_TOKEN_KEY, value: fakeToken });
      await Storage.set({ key: TOKEN_EXPIRY_KEY, value: expiryTime.toString() });
      await Storage.set({ key: TOKEN_PAYLOAD_KEY, value: JSON.stringify(payload) });

      this.tokenExpiryTime = expiryTime;
      this.tokenPayload = payload;

      console.warn('Usando autenticación anónima como fallback');
      return fakeToken;
    } catch (error) {
      console.error('Error en modo de compatibilidad:', error);
      return null;
    }
  }

  /**
   * Verifica si el token actual es válido
   */
  async validateToken(): Promise<boolean> {
    try {
      const token = await Storage.get({ key: FIREBASE_TOKEN_KEY });

      if (!token.value) {
        return false;
      }

      // Verificar el token con el servidor
      const response = await fetch(this.getValidateTokenEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.value}`
        }
      });

      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      console.error('Error al validar token:', error);
      return false;
    }
  }

  /**
   * Limpia el token almacenado al cerrar sesión
   */
  async clearToken(): Promise<void> {
    try {
      await Storage.remove({ key: FIREBASE_TOKEN_KEY });
      await Storage.remove({ key: TOKEN_EXPIRY_KEY });
      await Storage.remove({ key: TOKEN_PAYLOAD_KEY });
      this.tokenExpiryTime = 0;
      this.tokenPayload = null;
    } catch (error) {
      console.error('Error al limpiar token:', error);
    }
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    return this.tokenPayload !== null && Date.now() < this.tokenExpiryTime;
  }

  /**
   * Obtiene el ID del usuario actual
   */
  getCurrentUserId(): string | null {
    return this.tokenPayload?.uid || null;
  }

  /**
   * Obtiene la cédula del usuario actual
   */
  getCurrentUserCed(): string | null {
    return this.tokenPayload?.ced || null;
  }

  /**
   * Obtiene el código de evento del usuario actual
   */
  getCurrentEventCode(): string | null {
    return this.tokenPayload?.eventcode || null;
  }

  /**
   * Obtiene el rol del usuario actual
   */
  getCurrentUserRole(): string | null {
    return this.tokenPayload?.role || null;
  }

  /**
   * Obtiene el token actual
   */
  async getCurrentToken(): Promise<string | null> {
    const tokenResponse = await Storage.get({ key: FIREBASE_TOKEN_KEY });
    return tokenResponse.value;
  }
}

export default FirebaseTokenService;
